import { computed } from 'vue';

export function useBusinessHours() {
  // Check if business is currently open
  const isBusinessOpen = computed(() => {
    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour + (currentMinute / 60);
    
    // Business hours: Monday (1) to Friday (5), 9 AM to 5 PM EST
    const isWeekday = currentDay >= 1 && currentDay <= 5;
    const isDuringBusinessHours = currentTime >= 9 && currentTime < 17; // 9 AM to 5 PM
    
    return isWeekday && isDuringBusinessHours;
  });
  
  // Check if closing soon (within 30 minutes)
  const isClosingSoon = computed(() => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour + (currentMinute / 60);
    
    // Check if we're within 30 minutes of closing (4:30 PM - 5:00 PM on weekdays)
    const isWeekday = currentDay >= 1 && currentDay <= 5;
    const isClosingTime = currentTime >= 16.5 && currentTime < 17; // 4:30 PM to 5 PM
    
    return isWeekday && isClosingTime;
  });
  
  // Check if opening soon (within 30 minutes)
  const isOpeningSoon = computed(() => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const currentTime = currentHour + (currentMinute / 60);
    
    // Check if we're within 30 minutes of opening (8:30 AM - 9:00 AM on weekdays)
    const isWeekday = currentDay >= 1 && currentDay <= 5;
    const isOpeningTime = currentTime >= 8.5 && currentTime < 9; // 8:30 AM to 9 AM
    
    return isWeekday && isOpeningTime;
  });
  
  // Comprehensive business status with messaging
  const businessStatus = computed(() => {
    if (isBusinessOpen.value) {
      if (isClosingSoon.value) {
        return {
          status: 'closing-soon',
          message: 'Open - Closing Soon!',
          subtitle: 'We close at 5 PM today',
          actionText: 'Chat now before we close',
          guidance: 'Need immediate assistance? Call us at (954) 840-8866 or leave a message and we\'ll respond first thing tomorrow.'
        };
      }
      return {
        status: 'open',
        message: 'We\'re Open Now!',
        subtitle: 'Our licensed agents are available to help',
        actionText: 'Chat with an agent now',
        guidance: 'Our team is ready to help with all your insurance needs.'
      };
    } else {
      const now = new Date();
      const currentDay = now.getDay();
      const currentHour = now.getHours();
      
      if (isOpeningSoon.value) {
        return {
          status: 'opening-soon',
          message: 'Opening Soon!',
          subtitle: 'We open at 9 AM today',
          actionText: 'Call (954) 840-8866 or email us',
          guidance: 'We\'ll be with you shortly! You can also call us at (954) 840-8866 starting at 9 AM.'
        };
      }
      
      if (currentDay === 0 || currentDay === 6) {
        // Weekend
        return {
          status: 'closed',
          message: 'Closed - Weekend',
          subtitle: 'We\'ll respond first thing Monday morning',
          actionText: 'Call (954) 840-8866 or email us',
          guidance: 'You can call (954) 840-8866 to leave a voicemail or email us. We\'ll respond first thing Monday morning.'
        };
      } else if (currentHour < 9) {
        // Before business hours on weekday
        return {
          status: 'closed',
          message: 'Closed - Opens at 9 AM',
          subtitle: 'We\'ll be with you shortly after we open',
          actionText: 'Call (954) 840-8866 or email us',
          guidance: 'You can call us starting at 9 AM at (954) 840-8866, or email us now for priority handling.'
        };
      } else {
        // After business hours on weekday  
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowDay = tomorrow.getDay();
        
        if (tomorrowDay === 6) { // Tomorrow is Saturday
          return {
            status: 'closed',
            message: 'Closed - Opens Monday 9 AM',
            subtitle: 'We\'ll respond first thing Monday morning',
            actionText: 'Call (954) 840-8866 or email us',
            guidance: 'You can call (954) 840-8866 to leave a voicemail or email us. We\'ll respond first thing Monday morning.'
          };
        } else {
          return {
            status: 'closed',
            message: 'Closed - Opens at 9 AM',
            subtitle: 'We\'ll respond first thing tomorrow morning',
            actionText: 'Call (954) 840-8866 or email us',
            guidance: 'You can call (954) 840-8866 to leave a voicemail or email us. We\'ll respond first thing tomorrow morning.'
          };
        }
      }
    }
  });

  return {
    isBusinessOpen,
    isClosingSoon,
    isOpeningSoon,
    businessStatus
  };
} 